// Generative-UI renderer for an agent-authored Vega-Lite v5 spec. Open-ended
// counterpart to ChartArtifactCard (typed enum): the `spec` prop is passed
// through to vega-embed as-is — no wrapper schema — so the model's judgment
// fully controls marks/encodings/transforms/facets. vega-embed/vega-lite/vega
// are declarative renderers (no eval/code-exec), same trust model as Recharts.

import { useEffect, useRef, useState } from 'react';
import type { Result } from 'vega-embed';
import { Download } from 'lucide-react';

const actionButtonClassName =
  'flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground';

export function VegaLiteChart({ spec }: { spec: object }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<Result['view'] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    const el = containerRef.current;
    if (!el) return;

    import('vega-embed').then(({ default: embed }) => {
      embed(el, { ...spec, width: 'container' } as object, { actions: false })
        .then((result) => {
          if (cancelled) {
            result.view.finalize();
            return;
          }
          viewRef.current = result.view;
        })
        .catch((err: unknown) => {
          if (!cancelled) setError(err instanceof Error ? err.message : 'Invalid Vega-Lite spec');
        });
    });

    return () => {
      cancelled = true;
      viewRef.current?.finalize();
      viewRef.current = null;
    };
  }, [spec]);

  async function handleDownload() {
    const view = viewRef.current;
    if (!view) return;
    const url = await view.toImageURL('png');
    const a = document.createElement('a');
    a.href = url;
    a.download = 'chart.png';
    a.click();
    // TODO: hook a "share as persistent artifact" action here once a
    // download/artifact API exists for this content type.
  }

  if (error) {
    return (
      <div className="my-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
        Couldn't render chart: {error}
      </div>
    );
  }

  return (
    <div className="my-2 rounded-lg border border-border bg-card p-3">
      <div className="mb-1 flex justify-end">
        <button type="button" aria-label="Download chart" className={actionButtonClassName} onClick={handleDownload}>
          <Download className="size-4" />
        </button>
      </div>
      <div ref={containerRef} className="w-full" />
    </div>
  );
}
